/**
 * カードタブ（README ㊺〜53）。手札の弧・札が届く・相手と場所の選び方・使った瞬間・使用中の効果・受けた通知・使えない理由・開き方。
 * 得点や効果の判定は全てサーバー。ここは get_state を描き、use_card を呼ぶだけ。
 */
import './cards.css';
import { deckle } from '../../core/deckle';
import type { Tab, TabContext, TabFactory, TabShowOptions } from '../contract';
import type { GameState } from '../../api/types';
import { useCard } from '../../api/rpc';
import { toRpcError } from '../../api/errors';
import { fmtRemaining, serverNow } from '../../core/clock';
import { CARD_ICON_PATHS } from './icons';
import {
  arcSlots,
  cardInfo,
  countdownText,
  deriveNotices,
  deriveRows,
  errorText,
  finishedDuels,
  jellyText,
  preDisableReason,
  teamOptions,
  teamPickable,
  type EffectRow,
} from './logic';

const SVG_NS = 'http://www.w3.org/2000/svg';
const wait = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

interface DispEntry {
  cardId: string;
  /** 弧に置いてある team_card_id */
  ids: number[];
}

interface RowView {
  el: HTMLElement;
  sig: string;
  w: HTMLElement;
  left: HTMLElement;
  leftB: HTMLElement;
  bar: HTMLElement;
  barI: HTMLElement;
}

export const createCardsTab: TabFactory = (ctx: TabContext): Tab => {
  // ---------------------------------------------------------------- DOM
  const root = el('div', 'cd-root');
  // 台紙：お題タブと同じ紙1枚（帯の下6pxから下部タブの裏まで、耳付き）
  const sheet = el('div', 'cd-sheet');
  const sheetFace = el('div', 'cd-sheet-face');
  sheet.appendChild(sheetFace);
  root.appendChild(sheet);
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(() => {
      if (sheetFace.offsetWidth > 0) sheetFace.style.setProperty('--deckle', deckle(sheetFace.offsetWidth, sheetFace.offsetHeight, 6131));
    }).observe(sheetFace);
  }
  const scroll = el('div', 'cd-scroll');
  const tlow = el('div', 'cd-tlow');
  const fxw = el('div', 'cd-fxw');
  const nx = el('div', 'cd-nx');
  const hr = el('div', 'cd-hr');
  hr.append(nx);
  tlow.append(el('h2', 'cd-h', '使用中'), fxw, hr);
  const arc = el('div', 'cd-arc');
  const tsel = el('div', 'cd-tsel');
  const tsh = el('div', 'cd-tsh');
  const cn1 = el('b', 'cd-cn1');
  const tcnt = el('span', 'cd-tcnt');
  tsh.append(cn1, tcnt);
  const cd1 = el('span', 'cd-cd1');
  const ubox = el('div', 'cd-ubox');
  const use = el('button', 'cd-use', '使う');
  use.type = 'button';
  ubox.append(use);
  tsel.append(tsh, cd1, ubox);
  scroll.append(tlow, arc, tsel);
  const veil = el('div', 'cd-veil');
  root.append(scroll, veil);

  // ---------------------------------------------------------------- 状態
  let state: GameState | null = null;
  let visible = false;
  let initialized = false;
  let selected: string | null = null;
  let disp: DispEntry[] = [];
  /** 飛んでくる途中で、弧の場所だけ空けている札 */
  const hiding = new Set<string>();
  const known = new Set<number>();
  const spent = new Set<number>();
  const seenNotice = new Set<string>();
  const resultShown = new Set<number>();
  const results = new Map<number, { row: EffectRow; text: string; until: number }>();
  const snap = new Map<string, EffectRow>();
  const rowViews = new Map<string, RowView>();
  const refreshedFor = new Set<string>();
  let busy = false;
  let incoming: { cardId: string; id: number }[] = [];
  let arriving = false;
  let tickTimer: ReturnType<typeof setInterval> | null = null;
  let flash = '';
  let flashTimer: ReturnType<typeof setTimeout> | null = null;
  // 相手と場所の選び方
  let chooseOpen = false;
  let chooseAnim = false;
  let pickT: string | null = null;
  let pickA: string | null = null;

  const frontUrl = (cardId: string): string => ctx.asset(`cards/front_${cardId}.jpg`);
  const preloaded = new Set<string>();
  function preload(cardId: string): void {
    if (preloaded.has(cardId) || !cardInfo(cardId)) return;
    preloaded.add(cardId);
    const img = new Image();
    img.src = frontUrl(cardId);
    img.decode?.().catch(() => undefined);
  }

  const nowMs = (): number => serverNow();
  const rel = (e: Element): { x: number; y: number; w: number; h: number } => {
    const r = e.getBoundingClientRect();
    const s = root.getBoundingClientRect();
    return { x: r.left - s.left, y: r.top - s.top, w: r.width, h: r.height };
  };

  // ---------------------------------------------------------------- 札の見た目
  function cardName(cardId: string): string {
    return state?.cards.find((c) => c.card_id === cardId)?.name || cardInfo(cardId)?.name || cardId;
  }
  function cardDesc(cardId: string): string {
    return state?.cards.find((c) => c.card_id === cardId)?.description || cardInfo(cardId)?.desc || '';
  }

  /** 札の中身（線画と名前）。弧・飛んでくる札・裏返る札で同じ */
  function fillCard(target: HTMLElement, cardId: string): void {
    const path = CARD_ICON_PATHS[cardId];
    const art = el('span', 'cd-art');
    if (path) {
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('viewBox', '0 0 48 48');
      svg.setAttribute('aria-hidden', 'true');
      const p = document.createElementNS(SVG_NS, 'path');
      p.setAttribute('d', path);
      svg.append(p);
      art.append(svg);
    }
    const tn = el('span', 'cd-tn');
    const label = cardInfo(cardId)?.label;
    if (label) {
      tn.append(document.createTextNode(label[0]));
      if (label[1]) tn.append(document.createElement('wbr'), document.createTextNode(label[1]));
    } else tn.textContent = cardName(cardId);
    target.append(art, tn);
  }

  function makeCard(cardId: string): HTMLButtonElement {
    const b = el('button', 'cd-tc');
    b.type = 'button';
    b.dataset.id = cardId;
    fillCard(b, cardId);
    b.addEventListener('click', () => {
      if (chooseOpen || chooseAnim || busy) return;
      select(cardId);
    });
    return b;
  }

  function renderArc(): void {
    const W = arc.offsetWidth || 358;
    const slots = arcSlots(disp.length, W);
    const have = new Map<string, HTMLButtonElement>();
    for (const c of Array.from(arc.children)) if (c instanceof HTMLButtonElement && c.dataset.id) have.set(c.dataset.id, c);
    disp.forEach((d, i) => {
      let b = have.get(d.cardId);
      if (!b) {
        b = makeCard(d.cardId);
        arc.append(b);
      }
      have.delete(d.cardId);
      const n = d.ids.length;
      let x = b.querySelector('.cd-x');
      if (n > 1) {
        if (!x) {
          x = el('span', 'cd-x');
          b.append(x);
        }
        const t = `×${n}`;
        if (x.textContent !== t) x.textContent = t;
      } else x?.remove();
      b.classList.toggle('cd-stack', n > 1);
      b.classList.toggle('cd-wait', hiding.has(d.cardId));
      const s = slots[i];
      if (s) {
        b.style.left = `${s.left}px`;
        b.style.rotate = `${s.rot}deg`;
        b.style.translate = `0 ${s.ty}px`;
      }
      b.style.zIndex = String(i);
      b.setAttribute('aria-pressed', d.cardId === selected ? 'true' : 'false');
    });
    for (const stale of have.values()) stale.remove();
  }

  function select(cardId: string | null): void {
    selected = cardId;
    flash = '';
    if (flashTimer) clearTimeout(flashTimer);
    for (const c of Array.from(arc.children)) c.setAttribute('aria-pressed', (c as HTMLElement).dataset.id === cardId ? 'true' : 'false');
    updateSel();
  }

  function updateSel(): void {
    const d = disp.find((x) => x.cardId === selected);
    if (!d) {
      cn1.textContent = '札がありません';
      tcnt.replaceChildren();
      cd1.textContent = '';
      ubox.style.visibility = 'hidden';
      return;
    }
    ubox.style.visibility = '';
    cn1.textContent = cardName(d.cardId);
    cd1.textContent = cardDesc(d.cardId);
    if (d.ids.length > 1) {
      const b = el('b', undefined, String(d.ids.length));
      tcnt.replaceChildren(b, document.createTextNode('枚'));
    } else tcnt.replaceChildren();
    refreshUse();
  }

  // ---------------------------------------------------------------- 「使う」ボタンと理由（52）
  function currentReason(): string {
    if (flash) return flash;
    if (!selected || !state) return '';
    const code = preDisableReason(selected, state, nowMs());
    return code ? errorText(code) : '';
  }
  function refreshUse(): void {
    const r = currentReason();
    use.classList.toggle('cd-no', !!r);
    use.setAttribute('aria-disabled', r ? 'true' : 'false');
    const t = r || '使う';
    if (use.textContent !== t) use.textContent = t;
  }
  function flashMsg(text: string): void {
    flash = text;
    refreshUse();
    if (flashTimer) clearTimeout(flashTimer);
    flashTimer = setTimeout(() => {
      flash = '';
      refreshUse();
    }, 3000);
  }

  // ---------------------------------------------------------------- 使用中の欄（㊿・51）
  const rowSig = (r: EffectRow): string => `${r.received}|${r.text}|${r.res ?? ''}|${r.mapLink ?? ''}|${r.duel ? `${r.duel.who}/${r.duel.me}/${r.duel.op}` : ''}`;

  function fillWhat(w: HTMLElement, r: EffectRow): void {
    w.replaceChildren();
    if (r.res) {
      w.append(el('span', 'cd-res', r.res));
    } else if (r.duel) {
      w.append(
        document.createTextNode(`${r.duel.who}と `),
        el('b', 'cd-num', String(r.duel.me)),
        document.createTextNode(' 対 '),
        el('b', 'cd-num', String(r.duel.op)),
      );
    } else if (r.mapLink) {
      const go = el('span', 'cd-go', r.text);
      go.addEventListener('click', () => ctx.switchTab('map', { scout: true }));
      w.append(go);
    } else w.textContent = r.text;
  }

  function createRowView(r: EffectRow): RowView {
    const e = el('div', 'cd-fe');
    const w = el('span', 'cd-w');
    const leftB = el('b', 'cd-num');
    const left = el('span', 'cd-left');
    left.append(document.createTextNode('残り '), leftB);
    const barI = document.createElement('i');
    const bar = el('span', 'cd-bar');
    bar.append(barI);
    e.append(el('b', undefined, r.name), w, left, bar);
    return { el: e, sig: '', w, left, leftB, bar, barI };
  }

  function syncRows(animate: boolean): void {
    const now = nowMs();
    let rows: EffectRow[] = state ? deriveRows(state, now) : [];
    for (const r of rows) snap.set(r.key, r);
    for (const [id, res] of Array.from(results)) {
      if (res.until <= now) {
        results.delete(id);
        continue;
      }
      rows = rows.filter((r) => r.key !== res.row.key);
      rows.push({ ...res.row, res: res.text, waiting: false });
    }
    rows.sort((a, b) => a.startMs - b.startMs || a.key.localeCompare(b.key));
    const keys = new Set(rows.map((r) => r.key));
    for (const [key, v] of Array.from(rowViews)) {
      if (keys.has(key)) continue;
      rowViews.delete(key);
      snap.delete(key);
      if (animate) {
        v.el.classList.add('cd-out');
        setTimeout(() => v.el.remove(), 400);
      } else v.el.remove();
    }
    rows.forEach((r, i) => {
      let v = rowViews.get(r.key);
      if (!v) {
        v = createRowView(r);
        rowViews.set(r.key, v);
        if (animate) {
          v.el.classList.add('cd-in');
          const e = v.el;
          requestAnimationFrame(() => requestAnimationFrame(() => e.classList.remove('cd-in')));
        }
      }
      v.el.classList.toggle('cd-rv', r.received);
      const sig = rowSig(r);
      if (sig !== v.sig) {
        fillWhat(v.w, r);
        v.sig = sig;
      }
      const total = Math.max(1, r.endMs - r.startMs);
      const remain = Math.max(0, r.endMs - now);
      const timed = !r.res && Number.isFinite(r.endMs);
      v.left.style.display = timed ? '' : 'none';
      v.bar.style.display = timed ? '' : 'none';
      if (timed) {
        const t = fmtRemaining(remain);
        if (v.leftB.textContent !== t) v.leftB.textContent = t;
        v.barI.style.transform = `scaleX(${Math.min(1, remain / total)})`;
      }
      if (r.waiting && !refreshedFor.has(r.key)) {
        refreshedFor.add(r.key);
        void ctx.refresh();
      }
      if (fxw.children[i] !== v.el) fxw.insertBefore(v.el, fxw.children[i] ?? null);
    });
    const none = fxw.querySelector('.cd-none');
    if (rows.length === 0 && fxw.querySelectorAll('.cd-fe').length === 0) {
      if (!none) fxw.append(el('div', 'cd-none', 'なし'));
    } else none?.remove();
  }

  function syncCountdown(): void {
    const t = state ? countdownText(state.event.next_card_at, nowMs()) : null;
    if (t === null) {
      nx.replaceChildren();
      return;
    }
    let b = nx.querySelector('b');
    if (!b) {
      b = el('b', 'cd-num');
      nx.replaceChildren(document.createTextNode('次の札まで'), b);
    }
    if (b.textContent !== t) b.textContent = t;
  }

  function tick(): void {
    if (!visible) return;
    syncCountdown();
    refreshUse();
    syncRows(true);
  }

  // ---------------------------------------------------------------- 手札の同期と、届く瞬間（㊼）
  function addId(cardId: string, id: number): void {
    const d = disp.find((x) => x.cardId === cardId);
    if (d) {
      if (!d.ids.includes(id)) d.ids.push(id);
    } else disp.push({ cardId, ids: [id] });
  }

  function syncHand(s: GameState, animate: boolean): void {
    const live = s.cards
      .filter((c) => c.used_at === null && !spent.has(c.team_card_id))
      .sort((a, b) => Date.parse(a.acquired_at) - Date.parse(b.acquired_at) || a.team_card_id - b.team_card_id);
    const liveIds = new Set(live.map((c) => c.team_card_id));
    for (const d of disp) d.ids = d.ids.filter((id) => liveIds.has(id));
    disp = disp.filter((d) => d.ids.length > 0 || hiding.has(d.cardId));
    const fresh = live.filter((c) => !known.has(c.team_card_id));
    for (const c of fresh) {
      known.add(c.team_card_id);
      preload(c.card_id);
    }
    if (animate && initialized) {
      for (const c of fresh) incoming.push({ cardId: c.card_id, id: c.team_card_id });
    } else {
      for (const c of fresh) addId(c.card_id, c.team_card_id);
    }
    if (!selected || !disp.some((d) => d.cardId === selected)) selected = disp[0]?.cardId ?? null;
    renderArc();
    updateSel();
    if (incoming.length > 0) void runArrivals();
  }

  async function runArrivals(): Promise<void> {
    if (arriving) return;
    arriving = true;
    while (incoming.length > 0) {
      const batch = incoming;
      incoming = [];
      // 1枚のときだけ届いた札を選ぶ。複数枚のときは選んでいた札のまま（本人11/1）
      const single = batch.length === 1;
      const jobs: Promise<void>[] = [];
      for (const it of batch) {
        jobs.push(arrive(it.cardId, it.id, single));
        await wait(330);
      }
      await Promise.all(jobs);
    }
    arriving = false;
  }

  async function arrive(cardId: string, id: number, select1: boolean): Promise<void> {
    const fresh = !disp.some((d) => d.cardId === cardId);
    if (fresh) {
      hiding.add(cardId);
      disp.push({ cardId, ids: [id] });
      renderArc();
      await wait(60);
    }
    const slot = Array.from(arc.children).find((c) => (c as HTMLElement).dataset.id === cardId) as HTMLElement | undefined;
    const idx = disp.findIndex((d) => d.cardId === cardId);
    const S = arcSlots(disp.length, arc.offsetWidth || 358)[idx];
    if (slot && S) {
      const to = rel(slot);
      const toC = { x: to.x + to.w / 2, y: to.y + to.h / 2 };
      const f = el('div', 'cd-tc cd-fly');
      fillCard(f, cardId);
      root.append(f);
      const rr = root.getBoundingClientRect();
      const nxr = nx.getBoundingClientRect();
      const c0 = { x: rr.width / 2, y: nxr.height > 0 ? nxr.top - rr.top + nxr.height / 2 : toC.y };
      const at = (p: { x: number; y: number }, sc: number, rot: number): string =>
        `translate(${p.x - 40}px,${p.y - 68.5}px) rotate(${rot}deg) scale(${sc})`;
      f.animate(
        [
          { transform: at(c0, 0.35, 0), opacity: 0 },
          { transform: at(c0, 0.45, 0), opacity: 1, offset: 0.15 },
          { transform: at(toC, 1, S.rot) },
        ],
        { duration: 620, easing: 'cubic-bezier(.3,.7,.3,1)', fill: 'forwards' },
      );
      await wait(640);
      f.remove();
    }
    hiding.delete(cardId);
    if (!fresh) addId(cardId, id);
    renderArc();
    if (!fresh) {
      const x = arc.querySelector(`[data-id="${cardId}"] .cd-x`);
      if (x) {
        x.classList.remove('cd-pop');
        void (x as HTMLElement).offsetWidth;
        x.classList.add('cd-pop');
      }
    }
    if (select1 || !selected) select(cardId);
    else updateSel();
  }

  // ---------------------------------------------------------------- 相手と場所の選び方（㊽）
  const D = 300;
  const needOf = (cardId: string | null): string => (cardId ? cardInfo(cardId)?.need ?? '' : '');
  const ready = (): boolean => {
    const need = needOf(selected);
    return (!need.includes('t') || !!pickT) && (!need.includes('a') || !!pickA);
  };

  function buildChooser(cardId: string, s: GameState): HTMLElement {
    const need = needOf(cardId);
    const pk = el('div', 'cd-pk');
    if (need.includes('t')) {
      pk.append(el('h2', 'cd-h', 'チーム'));
      const g = el('div', 'cd-pk-g cd-pk-t');
      for (const t of teamOptions(s)) {
        const b = el('button', 'cd-op');
        b.type = 'button';
        b.dataset.t = t.id;
        b.setAttribute('aria-pressed', 'false');
        b.disabled = !teamPickable(cardId, t);
        b.append(el('b', undefined, t.name));
        if (t.rank !== null && t.total !== null) {
          const sp = el('span');
          sp.append(el('span', 'cd-num', String(t.rank)), document.createTextNode('位 '), el('span', 'cd-num', t.total.toLocaleString('en-US')), document.createTextNode('点'));
          b.append(sp);
        }
        g.append(b);
      }
      pk.append(g);
    }
    if (need.includes('a')) {
      pk.append(el('h2', 'cd-h', 'エリア'));
      const g = el('div', 'cd-pk-g cd-pk-a');
      for (const a of s.areas) {
        const b = el('button', 'cd-op');
        b.type = 'button';
        b.dataset.a = a.area_id;
        b.setAttribute('aria-pressed', 'false');
        if (a.color && /^#[0-9a-f]{3,8}$/i.test(a.color)) b.style.setProperty('--c', a.color);
        b.append(el('b', undefined, a.name));
        if (cardId === 'jelly') {
          const j = jellyText(a.multiplier, s.event.clear_step_multiplier, s.event.clear_max_multiplier);
          b.disabled = j.atMax;
          const mx = el('span', 'cd-mx');
          const small = (t: string): HTMLElement => el('small', undefined, t);
          if (j.atMax) {
            mx.append(small('×'), document.createTextNode(j.from));
            b.append(mx, el('span', undefined, '上限'));
          } else {
            const arrow = el('i', undefined, '→');
            mx.append(small('×'), document.createTextNode(j.from), arrow, small('×'), document.createTextNode(j.to));
            b.append(mx);
          }
        }
        g.append(b);
      }
      pk.append(g);
    }
    pk.addEventListener('click', (ev) => {
      const o = (ev.target as Element).closest('.cd-op') as HTMLButtonElement | null;
      if (!o || o.disabled) return;
      const isT = o.dataset.t !== undefined;
      for (const x of Array.from(pk.querySelectorAll(isT ? '[data-t]' : '[data-a]'))) x.setAttribute('aria-pressed', x === o ? 'true' : 'false');
      if (isT) pickT = o.dataset.t ?? null;
      else pickA = o.dataset.a ?? null;
      use.disabled = !ready();
    });
    return pk;
  }

  /** 札の弧の上端を使用中の欄の上端まで上げ、選ぶ欄が「使う」に届くならさらに上げる */
  function place(): void {
    const pk = tsel.querySelector<HTMLElement>('.cd-pk');
    if (!pk) return;
    const top = (e: Element): number => e.getBoundingClientRect().top;
    const cdB = cd1.offsetTop + cd1.offsetHeight;
    const uT = ubox.offsetTop;
    let up = -(top(arc) - parseFloat(scroll.style.getPropertyValue('--up') || '0') - top(tlow));
    const over = cdB + up + 12 + pk.offsetHeight - (uT - 12);
    if (over > 0) up -= over;
    scroll.style.setProperty('--up', `${up}px`);
    pk.style.top = `${cdB + up + 12}px`;
  }

  async function openChooser(): Promise<void> {
    if (!selected || !state) return;
    chooseAnim = true;
    chooseOpen = true;
    pickT = pickA = null;
    scroll.style.setProperty('--up', '0px');
    const pk = buildChooser(selected, state);
    tsel.append(pk);
    const stop = el('button', 'cd-stop', 'やめる');
    stop.type = 'button';
    ubox.prepend(stop);
    stop.addEventListener('click', () => void closeChooser());
    place();
    use.disabled = true;
    void pk.offsetWidth;
    scroll.classList.add('cd-ch');
    use.style.marginLeft = `${stop.offsetWidth + 8}px`;
    await wait(D + 20);
    chooseAnim = false;
  }

  async function closeChooser(): Promise<void> {
    if (chooseAnim || !chooseOpen) return;
    chooseAnim = true;
    scroll.classList.remove('cd-ch');
    use.style.marginLeft = '0px';
    await wait(D + 20);
    tsel.querySelector('.cd-pk')?.remove();
    ubox.querySelector('.cd-stop')?.remove();
    use.disabled = false;
    chooseOpen = false;
    pickT = pickA = null;
    chooseAnim = false;
  }

  // ---------------------------------------------------------------- 使う（㊾・52）
  function whom(): string {
    if (!state) return '';
    const t = pickT ? teamOptions(state).find((x) => x.id === pickT)?.name : '';
    const a = pickA ? state.areas.find((x) => x.area_id === pickA)?.name : '';
    return [t ? `${t}に` : '', a ? `${a}で` : ''].filter(Boolean).join(' ');
  }

  async function onUse(): Promise<void> {
    if (busy || chooseAnim || !selected || !state) return;
    if (use.classList.contains('cd-no')) return;
    const need = needOf(selected);
    if (need && !chooseOpen) {
      await openChooser();
      return;
    }
    if (need && !ready()) return;
    await doUse();
  }

  async function doUse(): Promise<void> {
    const cardId = selected;
    const d = disp.find((x) => x.cardId === cardId);
    const teamCardId = d?.ids[0];
    if (!cardId || teamCardId === undefined) return;
    busy = true;
    use.classList.add('cd-waiting');
    const sub = whom();
    let ok = false;
    try {
      await useCard({ teamCardId, targetTeam: pickT, areaId: pickA });
      ok = true;
    } catch (e) {
      flashMsg(errorText(toRpcError(e).code));
    }
    use.classList.remove('cd-waiting');
    if (!ok) {
      busy = false;
      void ctx.refresh();
      return;
    }
    spent.add(teamCardId);
    known.add(teamCardId);
    await playFlip(cardId, sub);
    if (chooseOpen) {
      await closeChooser();
    }
    spend(cardId, teamCardId);
    busy = false;
    await ctx.refresh();
    applyState();
  }

  /** 使った札を弧から1枚減らす。2枚以上なら枚数を、1枚なら弧から外して詰める */
  function spend(cardId: string, teamCardId: number): void {
    const d = disp.find((x) => x.cardId === cardId);
    if (d) d.ids = d.ids.filter((id) => id !== teamCardId);
    disp = disp.filter((x) => x.ids.length > 0);
    if (!disp.some((x) => x.cardId === selected)) selected = disp[0]?.cardId ?? null;
    renderArc();
    updateSel();
  }

  async function playFlip(cardId: string, sub: string): Promise<void> {
    const card = Array.from(arc.children).find((c) => (c as HTMLElement).dataset.id === cardId) as HTMLElement | undefined;
    if (!card) return;
    const from = rel(card);
    const f = el('div', 'cd-flip');
    const inner = el('div', 'cd-ui');
    const front = el('div', 'cd-uf cd-tc cd-ub');
    for (const ch of Array.from(card.children)) if (!ch.classList.contains('cd-x')) front.append(ch.cloneNode(true));
    const back = el('div', 'cd-uf cd-uo');
    if (cardInfo(cardId)) back.style.backgroundImage = `url("${frontUrl(cardId)}")`;
    inner.append(front, back);
    f.append(inner);
    root.append(f);
    card.style.visibility = 'hidden';
    const nm = el('div', 'cd-uname');
    nm.append(el('b', undefined, cardName(cardId)));
    if (sub) nm.append(el('span', undefined, sub));
    root.append(nm);

    const S = root.getBoundingClientRect();
    const W = 200;
    const sc = W / 80;
    const H = (W * 12) / 7;
    const cx = S.width / 2;
    const cy = S.height * 0.42;
    f.style.width = `${W}px`;
    f.style.setProperty('--z', String(sc));
    const at = (x: number, y: number, s: number): string => `translate(${x + 40 - W / 2}px,${y + 68.5 - H / 2}px) scale(${s / sc})`;
    const p0 = at(from.x + (from.w - 80) / 2, from.y + (from.h - 137) / 2, 1.08);
    const pc = (s = sc): string => at(cx - 40, cy - 68.5, s);
    f.style.transform = p0;
    veil.classList.add('cd-on');
    // 1 真ん中へ大きく（手札の面のまま）
    f.animate([{ transform: p0 }, { transform: pc() }], { duration: 420, easing: 'cubic-bezier(.3,.7,.3,1)', fill: 'forwards' });
    await wait(420);
    // 2 裏返る（途中で少しふくらむ）
    f.animate([{ transform: pc() }, { transform: pc(sc * 1.06), offset: 0.5 }, { transform: pc() }], { duration: 560, easing: 'ease-in-out', fill: 'forwards' });
    inner.animate([{ transform: 'rotateY(0deg)' }, { transform: 'rotateY(180deg)' }], { duration: 560, easing: 'ease-in-out', fill: 'forwards' });
    await wait(560);
    nm.style.top = `${cy + H / 2 + 16}px`;
    nm.classList.add('cd-on');
    await wait(1400);
    // 3 上へ薄れて消える
    nm.classList.remove('cd-on');
    f.animate([{ transform: pc(), opacity: 1 }, { transform: at(cx - 40, cy - 68.5 - 36, sc * 0.96), opacity: 0 }], { duration: 520, easing: 'ease-in', fill: 'forwards' });
    veil.classList.remove('cd-on');
    await wait(540);
    f.remove();
    nm.remove();
    card.style.visibility = '';
  }

  use.addEventListener('click', () => void onUse());

  // ---------------------------------------------------------------- 開き方（53）
  async function playOpen(): Promise<void> {
    root.classList.remove('cd-oin');
    void root.offsetWidth;
    root.classList.add('cd-oin');
    const cards = Array.from(arc.children) as HTMLElement[];
    for (const c of cards) c.getAnimations().forEach((a) => a.cancel());
    const w = arc.getBoundingClientRect();
    const cx = w.left + w.width / 2;
    for (const c of cards) {
      const r = c.getBoundingClientRect();
      const dx = cx - (r.left + r.width / 2);
      const rot = parseFloat(c.style.rotate) || 0;
      const sel = c.getAttribute('aria-pressed') === 'true';
      const k0 = { transform: `translate(${dx}px,${sel ? 26 : 12}px) rotate(${-rot}deg) scale(1)`, easing: 'cubic-bezier(.2,.8,.3,1)' };
      const k1 = { transform: 'translate(0px,0px) rotate(0deg) scale(1)' };
      if (sel) {
        c.animate([k0, { ...k1, offset: 450 / 650, easing: 'cubic-bezier(.3,.7,.3,1)' }, { transform: 'translate(0px,-14px) rotate(0deg) scale(1.08)' }], { duration: 650, delay: 60, fill: 'backwards' });
      } else c.animate([k0, k1], { duration: 450, delay: 60, fill: 'backwards' });
    }
    await wait(720);
    root.classList.remove('cd-oin');
  }

  // ---------------------------------------------------------------- 状態の反映
  function applyState(): void {
    if (!state) return;
    syncHand(state, visible && initialized);
    initialized = true;
    if (chooseOpen && !disp.some((x) => x.cardId === selected)) void closeChooser();
    syncCountdown();
    syncRows(visible);
    refreshUse();
  }

  addEventListener('resize', () => {
    if (!visible) return;
    renderArc();
    if (chooseOpen) place();
  });

  const tab: Tab = {
    id: 'cards',
    el: root,
    show(opts: TabShowOptions): Promise<void> | void {
      visible = true;
      // 隠れている間は弧の幅が取れていないので、見せる前に置き直す（滑らせない）
      arc.classList.add('cd-snap');
      renderArc();
      void arc.offsetWidth;
      arc.classList.remove('cd-snap');
      syncCountdown();
      syncRows(false);
      refreshUse();
      if (tickTimer) clearInterval(tickTimer);
      tickTimer = setInterval(tick, 1000);
      if (opts.animate && disp.length > 0) return playOpen();
      return undefined;
    },
    hide(): void {
      visible = false;
      if (tickTimer) clearInterval(tickTimer);
      tickTimer = null;
    },
    update(next: GameState, prev: GameState | null): void {
      state = next;
      // どの画面にいても出す通知
      for (const n of deriveNotices(prev, next, seenNotice)) ctx.notify(n);
      // 縄張り争いの結果（勝ち／負け）を3秒
      for (const fd of finishedDuels(next, prev, resultShown, (id) => rowViews.has(`e${id}`))) {
        resultShown.add(fd.effectId);
        const key = `e${fd.effectId}`;
        const row = snap.get(key);
        if (row) results.set(fd.effectId, { row, text: fd.outcome.text, until: nowMs() + 3000 });
      }
      // 使っている最中は手札と欄に触れない（演出のあとに反映する）
      if (busy) return;
      applyState();
    },
  };
  return tab;
};
